import React, { useEffect, useState } from 'react';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import TraitTrends from './TraitTrends';
import WritingDetail from './WritingDetail';
import { ShowMore, usePagedItems } from './usePagedItems';
import { dayMonth, trimNumber } from './lib/activityFormat';

/** The id of a piece's row, which the Feedback tab links to. */
export const writingRowId = (item) => `writing-${item.kind}-${item.id}`;

const Mark = ({ mark }) => {
  if (!mark) return <span className="td-act-meta">No mark</span>;
  return (
    <span className="td-act-mark">
      {trimNumber(mark.value)}
      <small>{` / ${mark.out_of}${mark.band ? ` · ${mark.band}` : ''}`}</small>
    </span>
  );
};

// A statement that opens with its own quotation (“A little learning…” Discuss.)
// is shown as stored, so it never gets a second pair of quotation marks.
const OPENS_QUOTED = /^["“‘']/;

const Subtitle = ({ item }) => {
  if (!item.subtitle) return null;
  // The statement the student answered.
  const text = OPENS_QUOTED.test(item.subtitle) ? item.subtitle : `“${item.subtitle}”`;
  return <div className="td-act-rowsub">{text}</div>;
};

const PieceRow = ({ item, open, commented, onToggle }) => {
  const id = writingRowId(item);
  return (
    <tr id={id} className="td-act-piece">
      <td className="td-act-when">{dayMonth(item.completed_at)}</td>
      <td>
        <div className="td-act-rowtitle">{item.title}</div>
        <Subtitle item={item} />
      </td>
      <td><Mark mark={item.mark} /></td>
      <td>{item.strongest || '—'}</td>
      <td>{item.to_work_on || '—'}</td>
      <td>
        {commented
          ? <span className="td-act-pill td-act-pill--g">Commented</span>
          : <span className="td-act-pill td-act-pill--b">Not yet</span>}
      </td>
      <td>
        <button
          type="button"
          className="td-act-caret"
          aria-expanded={open}
          aria-controls={`${id}-detail`}
          aria-label={`${open ? 'Hide' : 'Show'} details: ${item.title}, ${dayMonth(item.completed_at)}`}
          onClick={onToggle}
        >
          <span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
      </td>
    </tr>
  );
};

/**
 * Writing and feedback, from GET …/activity/writing: every scored TARA
 * and LNAT essay (exam writing only; a written case answer is in Cases),
 * newest first, each opening into its marking, the feedback the student
 * saw, comments and their text. A row
 * whose id is in the URL's hash opens when the list arrives, so a link from
 * the Feedback tab lands on the piece.
 */
const WritingSection = ({ state, trendsState, studentId, fetchPage, onCommentPosted }) => {
  const first = state.status === 'ready' ? state.data : null;
  const paged = usePagedItems(first, 'items', fetchPage);
  const [open, setOpen] = useState(() => new Set());
  const [commentedHere, setCommentedHere] = useState(() => new Set());

  useEffect(() => {
    if (!first || typeof window === 'undefined') return;
    const target = window.location.hash.slice(1);
    const match = first.items.find((item) => writingRowId(item) === target);
    if (!match) return;
    setOpen((prev) => new Set(prev).add(target));
    const row = document.getElementById(target);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'start' });
  }, [first]);

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const onCommented = (item, comment) => {
    setCommentedHere((prev) => new Set(prev).add(writingRowId(item)));
    if (onCommentPosted) onCommentPosted(item, comment);
  };

  return (
    <ActivitySection
      id="writing"
      title="Writing and feedback"
      description="Every scored TARA and LNAT essay. Open a row to see the marking, the feedback the student saw, and their essay."
    >
      <SectionState state={state} what="writing" emptyTitle="No scored writing in this period" />
      {first ? (
        <div className="td-card">
          <div className="td-act-scroll">
            <table className="td-act-wtable">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Task</th>
                  <th scope="col">Mark</th>
                  <th scope="col">Strongest</th>
                  <th scope="col">To work on</th>
                  <th scope="col">Your comment</th>
                  <th scope="col"><span className="td-act-sr">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => {
                  const id = writingRowId(item);
                  const isOpen = open.has(id);
                  return (
                    <React.Fragment key={id}>
                      <PieceRow
                        item={item}
                        open={isOpen}
                        commented={item.commented || commentedHere.has(id)}
                        onToggle={() => toggle(id)}
                      />
                      {isOpen ? (
                        <tr id={`${id}-detail`} className="td-act-detail">
                          <td colSpan={7}>
                            <WritingDetail studentId={studentId} item={item} onCommented={onCommented} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ShowMore paged={paged} what="writing" />
        </div>
      ) : null}
      <TraitTrends state={trendsState} />
    </ActivitySection>
  );
};

export default WritingSection;
